import React, { MouseEventHandler } from 'react';
import { Stack } from '@cp/ds/src/components/stack';
import { styled } from '@cp/ds/src/theme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme/const';
import { Title } from './title';

export interface CardHeaderProps {
  title: string;
  url?: string;
  onTitleClick?: MouseEventHandler<HTMLAnchorElement>;
  hotBadge?: React.ReactNode;
  /** Card interaction status: Replied, Viewed, or publication date (CreatedAt) */
  status: React.ReactNode;
}

const Root = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'hasHotBadge',
})<{ hasHotBadge: boolean }>(({ theme, hasHotBadge }) => ({
  display: 'grid',
  width: '100%',
  gap: theme.spacing(0.75),
  alignItems: 'center',
  gridTemplateColumns: hasHotBadge ? '1fr auto' : '1fr',
  gridTemplateAreas: hasHotBadge ? '"status hotBadge" "title title"' : '"status" "title"',
  [theme.breakpoints.up(MOBILE_BREAKPOINT_THRESHOLD)]: {
    alignItems: 'flex-start',
    gridTemplateColumns: hasHotBadge ? 'auto 1fr auto' : '1fr auto',
    gridTemplateAreas: hasHotBadge ? '"hotBadge title status"' : '"title status"',
  },
}));

const StatusSlot = styled('div')({
  gridArea: 'status',
  minWidth: 0,
});

const HotBadgeSlot = styled('div')({
  gridArea: 'hotBadge',
  flexShrink: 0,
});

const TitleSlot = styled('div')({
  gridArea: 'title',
  minWidth: 0,
});

export const CardHeader: React.FC<CardHeaderProps> = ({ title, url, onTitleClick, hotBadge, status }) => {
  const hasHotBadge = Boolean(hotBadge);

  return (
    <Root hasHotBadge={hasHotBadge}>
      <StatusSlot>{status}</StatusSlot>

      {hotBadge ? <HotBadgeSlot>{hotBadge}</HotBadgeSlot> : null}

      <TitleSlot>
        <Title onClick={onTitleClick} url={url}>
          {title}
        </Title>
      </TitleSlot>
    </Root>
  );
};
